import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { Search } from "@/components/search/Search.tsx";
import { EsignDocumentRow } from "@/pages/esign/widgets/EsignDocumentRow.tsx";
import { useEsignEnvelopesInfinite } from "@/queries/use-esign.query.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiEsignDocumentEnvelope,
  EsignEnvelopeStatus,
  EsignRequestStatus,
  EsignSignatureType,
} from "@/types/esign.types.ts";
import {
  Avatar,
  DotSeparator,
  FontAwesomeIcon,
  ListItem,
  ProgressBar,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Tag,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

interface EsignRequestListProps {
  documentId: number;
  documentTitle: string;
  completedEnvelopesCount: number;
  activeEnvelopesCount: number; // Без cancelled
  totalEnvelopesCount: number; // Все, включая cancelled
  showList: boolean;
  onOpenList: () => void;
  onSelectEnvelope: (envelope: ApiEsignDocumentEnvelope) => void;
}

export interface EsignRequestListRef {
  fetchMoreOnBottomReached: (scrollHeight: number, scrollTop: number, clientHeight: number) => void;
}

export const EsignRequestList = forwardRef<EsignRequestListRef, EsignRequestListProps>(
  function EsignRequestList(
    {
      documentId,
      documentTitle,
      completedEnvelopesCount,
      activeEnvelopesCount,
      totalEnvelopesCount,
      showList,
      onOpenList,
      onSelectEnvelope,
    },
    ref
  ) {
    const { t } = useTranslation();
    const [listTab, setListTab] = useState(0);

    const listTabs = [
      { title: t("common:all"), status: undefined },
      { title: t("core:esign_status_pending"), status: EsignEnvelopeStatus.pending },
      { title: t("common:status_completed"), status: EsignEnvelopeStatus.completed },
    ];
    const listStatus = listTabs[listTab].status;

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    useDebounce(() => setSearch(searchInput), 300, [searchInput]);

    const {
      data: listData,
      isLoading,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useEsignEnvelopesInfinite(
      {
        document_id: documentId,
        status: listStatus,
        search: search || undefined,
      },
      showList
    );

    const items = useMemo(
      () => listData?.pages.flatMap((page) => page?.items ?? []) ?? [],
      [listData]
    );

    useImperativeHandle(ref, () => ({
      fetchMoreOnBottomReached: (scrollHeight, scrollTop, clientHeight) => {
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
    }));

    const envelopesProgress =
      activeEnvelopesCount === 0
        ? 0
        : Math.round((completedEnvelopesCount / activeEnvelopesCount) * 100);

    if (showList) {
      return (
        <Stack>
          <Search
            placeholder={t("common:search")}
            value={searchInput}
            onInput={(e) => setSearchInput(e.currentTarget.value)}
            type="text"
            clearable
            onClear={() => setSearchInput("")}
          />
          <Tabs activeTab={listTab} onActiveTabChange={setListTab} fit="sm">
            {listTabs.map((tab) => (
              <TabItem key={tab.title} title={tab.title} />
            ))}
          </Tabs>
          <Stack gap="sm">
            {isLoading && (
              <>
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </>
            )}
            {!isLoading && items.length === 0 && <EmptyStateNothingFound />}
            {!isLoading &&
              items.map((item) => {
                const firstSigner = item.signers[0]?.employee;
                const restCount = item.signers.length - 1;
                const signerName = firstSigner
                  ? `${firstSigner.first_name} ${firstSigner.last_name}`
                  : t("core:esign_signers");
                const avatarUrl = firstSigner ? getEmployeeAvatarUrl(firstSigner, "md") : undefined;
                const isCompleted = item.status === EsignEnvelopeStatus.completed;
                const isCancelled = item.status === EsignEnvelopeStatus.cancelled;
                return (
                  <ListItem
                    key={item.public_id}
                    avatarUrl={avatarUrl}
                    forceShowAvatar
                    onClick={() => onSelectEnvelope(item)}
                    title={signerName}
                    titleSuffix={
                      restCount > 0 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Text className="text-secondary ml-1 cursor-help">{`+${restCount}`}</Text>
                          </TooltipTrigger>
                          <TooltipContent>
                            <Stack gap="xs" items="start">
                              {item.signers.map((signer, index) => {
                                const name = signer.employee
                                  ? `${signer.employee.first_name} ${signer.employee.last_name}`
                                  : "—";
                                const statusLabel =
                                  signer.status === EsignRequestStatus.signed
                                    ? item.signature_type === EsignSignatureType.signature
                                      ? t("core:esign_status_signed")
                                      : t("core:esign_status_acknowledged")
                                    : signer.status === EsignRequestStatus.cancelled
                                      ? t("core:esign_status_cancelled")
                                      : signer.status === EsignRequestStatus.declined
                                        ? t("core:esign_status_declined")
                                        : t("core:esign_status_pending");
                                return (
                                  <DotSeparator key={index} dotClassName="text-light">
                                    <Text>{name}</Text>
                                    <Text>{statusLabel}</Text>
                                  </DotSeparator>
                                );
                              })}
                            </Stack>
                          </TooltipContent>
                        </Tooltip>
                      ) : undefined
                    }
                    caption={t(
                      item.signature_type === EsignSignatureType.signature
                        ? "core:esign_signed_of"
                        : "core:esign_acknowledged_of",
                      { signed: item.signed_requests_count, total: item.total_requests_count }
                    )}
                    valueSlot={
                      <Tag
                        variant={isCompleted ? "positive" : isCancelled ? "secondary" : "warning"}
                      >
                        {isCompleted
                          ? t("common:status_completed")
                          : isCancelled
                            ? t("core:esign_status_cancelled")
                            : t("core:esign_status_pending")}
                      </Tag>
                    }
                  />
                );
              })}
            {isFetchingNextPage && <Skeleton className="h-14 w-full" />}
          </Stack>
        </Stack>
      );
    }

    return (
      <>
        <Stack gap="xs">
          <Title header={t("core:esign_personas")} size="xs" />
          <Stack gap="sm" className="mt-4">
            <Stack direction="horizontal" items="center" justify="between">
              <Text className="text-title">
                {t("core:esign_envelopes_completed_of", {
                  completed: completedEnvelopesCount,
                  total: activeEnvelopesCount,
                })}
              </Text>
              <Text className="text-secondary">{envelopesProgress}%</Text>
            </Stack>
            <ProgressBar
              completed={completedEnvelopesCount}
              total={activeEnvelopesCount}
              variant="status"
              tooltip={t("core:esign_envelopes_completed_of", {
                completed: completedEnvelopesCount,
                total: activeEnvelopesCount,
              })}
            />
          </Stack>
        </Stack>

        <Stack>
          <Title header={t("common:requests")} size="xs" />
          <ListItem
            icon={<Avatar icon={<FontAwesomeIcon icon="fa-light fa-users" />} size="md" />}
            title={t("core:esign_requests_count", { count: totalEnvelopesCount })}
            valueSlot={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
            onClick={onOpenList}
          />
        </Stack>

        <EsignDocumentRow documentId={documentId} documentTitle={documentTitle} />
      </>
    );
  }
);
